import { useRef, useState } from "react";

import { DEFAULT_FONT_FAMILY, DEFAULT_FONT_SIZE } from "@excalidraw/common";
import { newTextElement } from "@excalidraw/element";

import { useUIAppState } from "../../context/ui-appState";
import { useApp, useExcalidrawSetAppState } from "../App";
import { Dialog } from "../Dialog";
import { ArrowRightIcon } from "../icons";
import { withInternalFallback } from "../hoc/withInternalFallback";
import Spinner from "../Spinner";

import "./AIGenerateDialog.scss";

export type OnAIReadSubmit = (text: string) => Promise<{
  audioDataURL?: string | null;
  error?: Error | null;
}>;

export const AIReadDialog = (
  props: { onSubmit: OnAIReadSubmit } | { __fallback: true },
) => {
  const appState = useUIAppState();

  if (appState.openDialog?.name !== "ai-read") {
    return null;
  }

  return <AIReadDialogContent {...props} />;
};

const AIReadDialogContent = withInternalFallback(
  "AIReadDialogContent",
  (props: { onSubmit: OnAIReadSubmit } | { __fallback: true }) => {
    const app = useApp();
    const setAppState = useExcalidrawSetAppState();

    const [text, setText] = useState("");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [audioDataURL, setAudioDataURL] = useState<string | null>(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const audioRef = useRef<HTMLAudioElement | null>(null);

    const handleGenerate = async () => {
      if ("__fallback" in props || text.trim().length < 3 || loading) {
        return;
      }

      setLoading(true);
      setError(null);
      setAudioDataURL(null);

      try {
        const result = await props.onSubmit(text.trim());
        if (result.error) {
          setError(result.error.message);
        } else if (result.audioDataURL) {
          setAudioDataURL(result.audioDataURL);
        } else {
          setError("Nie udało się wygenerować mowy");
        }
      } catch (err: any) {
        setError(err?.message || "Błąd generowania");
      } finally {
        setLoading(false);
      }
    };

    const handlePlay = () => {
      if (!audioDataURL) {
        return;
      }

      if (audioRef.current) {
        if (isPlaying) {
          audioRef.current.pause();
          audioRef.current.currentTime = 0;
          setIsPlaying(false);
        } else {
          audioRef.current.play();
          setIsPlaying(true);
        }
      }
    };

    const handleInsert = () => {
      if (!audioDataURL || !text.trim()) {
        return;
      }

      // Create text element with speaker icon and audio data in customData
      const element = newTextElement({
        x: 100,
        y: 100,
        text: `🔊 ${text.trim()}`,
        fontSize: DEFAULT_FONT_SIZE,
        fontFamily: DEFAULT_FONT_FAMILY,
        textAlign: "left",
        verticalAlign: "top",
        customData: {
          ttsText: text.trim(),
          audioDataURL,
          isTTS: true,
        },
      });

      app.addElementsFromPasteOrLibrary({
        elements: [element],
        files: null,
        position: "center",
        fitToContent: true,
      });

      setAppState({ openDialog: null });
    };

    const isFallback = "__fallback" in props;

    return (
      <Dialog
        className="ai-generate-dialog"
        onCloseRequest={() => setAppState({ openDialog: null })}
        size={600}
        title="Generuj mowę AI"
      >
        <div className="ai-generate-dialog__content">
          {isFallback ? (
            <div className="ai-generate-dialog__fallback">
              Generowanie mowy AI nie jest dostępne w tej wersji.
            </div>
          ) : (
            <>
              <div className="ai-generate-dialog__input-section">
                <label className="ai-generate-dialog__label">
                  Wpisz tekst do przeczytania:
                </label>
                <textarea
                  className="ai-generate-dialog__textarea"
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  placeholder="np. Witaj w naszej lekcji o fotosytezie..."
                  rows={6}
                  maxLength={5000}
                />
                <div className="ai-generate-dialog__char-count">
                  {text.length}/5000 znaków
                </div>
                <button
                  className="ai-generate-dialog__button ai-generate-dialog__button--primary"
                  onClick={handleGenerate}
                  disabled={text.trim().length < 3 || loading}
                >
                  {loading ? <Spinner /> : <>{ArrowRightIcon} Generuj mowę</>}
                </button>
              </div>

              {error && (
                <div className="ai-generate-dialog__error">{error}</div>
              )}

              {audioDataURL && (
                <div className="ai-generate-dialog__preview">
                  <div className="ai-generate-dialog__audio-preview">
                    <audio
                      ref={audioRef}
                      src={audioDataURL}
                      onEnded={() => setIsPlaying(false)}
                    />
                    <button
                      className="ai-generate-dialog__button ai-generate-dialog__button--play"
                      onClick={handlePlay}
                    >
                      {isPlaying ? "⏹️ Stop" : "▶️ Odsłuchaj"}
                    </button>
                    <div className="ai-generate-dialog__preview-text ai-generate-dialog__preview-text--small">
                      🔊 {text.trim()}
                    </div>
                  </div>
                  <button
                    className="ai-generate-dialog__button ai-generate-dialog__button--success"
                    onClick={handleInsert}
                  >
                    Wstaw na płótno
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </Dialog>
    );
  },
);

